import { loadProductReviews } from '@theme/casa-review-data';

const MAX_RATING = 5;

function makeElement(tag, className, text) {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}
function ratingStars(rating) {
  const stars = makeElement('span', 'casa-rating-stars', '★★★★★');
  stars.style.setProperty('--rating-fill', `${rating * 20}%`);
  stars.setAttribute('aria-hidden', 'true');
  stars.dir = 'ltr';
  return stars;
}
function summaryContent(data) {
  const fragment = document.createDocumentFragment();
  if (data.totalCount) fragment.append(ratingStars(data.averageRating), ` ${data.averageRating.toFixed(1)}/5 · `);
  fragment.append(`${data.totalCount} ${data.totalCount === 1 ? 'review' : 'reviews'}`);
  return fragment;
}
function scrollToReviews(event, targetId) {
  const target = document.getElementById(targetId);
  if (!target) return;
  event.preventDefault();
  target.focus({ preventScroll: true });
  target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
  history.replaceState(null, '', `#${targetId}`);
}

class CasaReviewSummary extends HTMLElement {
  // Imported marketplace counts are server-rendered and must not be replaced by Yozo's 0.
  get hasImportedCount() {
    return Number(this.dataset.importedCount) > 0;
  }
  // Server-rendered average of the imported reviews for this picker group.
  get importedRating() {
    return Number(this.dataset.importedRating) || MAX_RATING;
  }
  connectedCallback() {
    if (this.shadowRoot) return;
    const root = this.attachShadow({ mode: 'open' });
    const stylesheet = makeElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = this.dataset.stylesheet;
    const link = makeElement('a', 'casa-review-summary');
    if (this.hasImportedCount) {
      link.classList.add('casa-review-summary--imported');
      link.append(...this.importedSummary());
    } else {
      link.textContent = this.querySelector('a')?.textContent.trim() || 'Customer reviews';
    }
    link.href = `#${this.dataset.target}`;
    link.addEventListener('click', event => scrollToReviews(event, this.dataset.target));
    root.append(stylesheet, link);
    if (this.hasImportedCount) return;
    loadProductReviews({ store: this.dataset.store, productId: this.dataset.productId })
      .then(data => this.renderSummary(data))
      .catch(() => {});
  }
  // Stars with the server-translated rating label, plus the translated count, from the light-DOM link.
  importedSummary() {
    const label = this.querySelector('.casa-review-summary__count')?.textContent ?? this.textContent;
    const count = makeElement('span', 'casa-review-summary__count', label.trim());
    const stars = ratingStars(this.importedRating);
    const ratingLabel = this.querySelector('.casa-rating-stars')?.getAttribute('aria-label');
    if (ratingLabel) {
      stars.removeAttribute('aria-hidden');
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', ratingLabel);
    }
    return [stars, count];
  }
  // Without reviews the link keeps its translated label instead of showing 0.
  renderSummary(data) {
    if (this.hasImportedCount || !data.totalCount) return;
    this.shadowRoot?.querySelector('a')?.replaceChildren(summaryContent(data));
  }
}

class CasaProductReviews extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    if (!this.shadowRoot) {
      const template = this.querySelector('template');
      if (!template) return;
      this.attachShadow({ mode: 'open' }).append(template.content.cloneNode(true));
    }
    // Keep fetched content outside Horizon's light-DOM recommendation hydration.
    const root = this.shadowRoot;
    this.track = root.querySelector('.casa-pdp-reviews__track');
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.dataset.autoplay !== 'true';
    this.visible = false;
    this.hovered = false;
    this.focused = false;
    this.page = 0;
    this.pauseButton = root.querySelector('[data-review-pause]');
    this.position = root.querySelector('[data-review-position]');
    this.controls = root.querySelector('.casa-pdp-reviews__controls');
    this.status = root.querySelector('.casa-pdp-reviews__status');
    this.moreButton = root.querySelector('[data-review-more]');
    this.retryButton = root.querySelector('[data-review-retry]');
    root.querySelector('[data-review-prev]').addEventListener('click', () => this.move(-1, true), options);
    root.querySelector('[data-review-next]').addEventListener('click', () => this.move(1, true), options);
    this.pauseButton.addEventListener('click', () => { this.paused = !this.paused; this.schedule(); }, options);
    this.moreButton.addEventListener('click', () => this.loadPage(this.page + 1), options);
    this.retryButton.addEventListener('click', () => this.loadPage(this.page + 1), options);
    this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, options);
    this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, options);
    this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, options);
    this.addEventListener('focusout', event => { this.focused = this.contains(event.relatedTarget); this.schedule(); }, options);
    this.track.addEventListener('scroll', () => this.updatePosition(), { ...options, passive: true });
    this.track.addEventListener('pointerdown', () => { this.paused = true; this.schedule(); }, options);
    document.addEventListener('visibilitychange', () => this.schedule(), options);
    this.motion.addEventListener('change', () => this.schedule(), options);
    this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.schedule(); }, { threshold: 0.15 });
    this.observer.observe(this);
    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);
    this.loadPage(1);
  }
  async loadPage(page) {
    if (this.loading) return;
    const controller = this.controller;
    this.loading = true;
    this.setAttribute('aria-busy', 'true');
    this.moreButton.disabled = true;
    this.retryButton.hidden = true;
    try {
      const data = await loadProductReviews({ store: this.dataset.store, productId: this.dataset.productId, page });
      if (controller !== this.controller || controller.signal.aborted) return;
      document.querySelectorAll('casa-review-summary').forEach(summary => {
        if (summary.dataset.productId === this.dataset.productId && summary.dataset.store === this.dataset.store) summary.renderSummary?.(data);
      });
      const cards = data.reviews.map(review => this.reviewCard(review));
      if (page === 1) this.track.replaceChildren(...cards);
      else this.track.append(...cards);
      this.page = page;
      this.track.hidden = !this.track.children.length;
      this.track.dataset.count = String(this.track.children.length);
      const aggregate = this.shadowRoot.querySelector('.casa-pdp-reviews__aggregate');
      aggregate.replaceChildren(summaryContent(data));
      aggregate.hidden = !data.totalCount;
      this.status.hidden = data.totalCount > 0;
      this.status.textContent = 'No reviews yet. Be the first to share your experience.';
      this.moreButton.hidden = !data.hasMore;
      const writeLink = this.shadowRoot.querySelector('[data-review-write]');
      const writeUrl = new URL(`https://agents.yozo.ai/reviews/public/${data.shopId}`);
      writeUrl.search = new URLSearchParams({ source: 'storefront-write-review', handle: this.dataset.productHandle, product_id: this.dataset.productId });
      writeLink.href = writeUrl.href;
      writeLink.hidden = false;
      this.updateControls();
      if (this.dataset.revealText === 'true') this.observeReviewText();
    } catch {
      if (controller !== this.controller || controller.signal.aborted) return;
      this.status.hidden = false;
      this.status.textContent = 'Reviews are temporarily unavailable. Please try again.';
      this.retryButton.hidden = false;
    } finally {
      if (controller === this.controller) {
        this.loading = false;
        this.moreButton.disabled = false;
        this.setAttribute('aria-busy', 'false');
      }
    }
  }
  reviewCard(review) {
    const article = makeElement('article', 'casa-pdp-reviews__card');
    article.setAttribute('role', 'group');
    article.setAttribute('aria-roledescription', 'slide');
    const rating = makeElement('p', 'casa-pdp-reviews__rating');
    rating.setAttribute('aria-label', `${review.rating} out of 5 stars`);
    rating.append(ratingStars(review.rating), ` ${review.rating}/5`);
    const text = review.body || review.title;
    const quote = makeElement('blockquote', 'casa-review-text', text);
    quote.lang = /[\u0600-\u06ff]/u.test(text) ? 'ar' : 'en';
    quote.dir = 'auto';
    article.append(rating, quote, makeElement('p', 'casa-pdp-reviews__name', review.name || 'Anonymous'));
    if (review.verifiedBuyer) article.append(makeElement('p', 'casa-pdp-reviews__verified', 'Verified purchase'));
    return article;
  }
  observeReviewText() {
    this.textObserver?.disconnect();
    this.textObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        this.textObserver.unobserve(entry.target);
        if (this.motion.matches || entry.target.dataset.revealed) continue;
        entry.target.dataset.revealed = 'true';
        const rtl = getComputedStyle(entry.target).direction === 'rtl';
        entry.target.animate([{ clipPath: rtl ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)' }, { clipPath: 'inset(0)' }], { duration: 1600, easing: 'ease-in-out' });
      }
    }, { threshold: 0.3 });
    this.track.querySelectorAll('blockquote').forEach(quote => this.textObserver.observe(quote));
  }
  index() {
    if (!this.track.children.length) return 0;
    const left = this.track.scrollLeft;
    const start = this.track.children[0].offsetLeft;
    return [...this.track.children].reduce((best, card, index, cards) =>
      Math.abs(card.offsetLeft - start - left) < Math.abs(cards[best].offsetLeft - start - left) ? index : best, 0);
  }
  updatePosition() { this.position.textContent = `${this.track.children.length ? this.index() + 1 : 0} / ${this.track.children.length}`; }
  updateControls() {
    this.controls.hidden = this.track.children.length < 2 || this.track.scrollWidth <= this.track.clientWidth + 2;
    [...this.track.children].forEach((card, index) => card.setAttribute('aria-label', `${index + 1} of ${this.track.children.length}`));
    this.updatePosition();
    this.schedule();
  }
  move(direction, manual = false) {
    const cards = this.track.children;
    if (!cards.length) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const atEnd = this.track.scrollLeft >= max - 2;
    const next = direction > 0 ? (atEnd ? 0 : Math.min(cards.length - 1, this.index() + 1)) : (this.track.scrollLeft <= 2 ? cards.length - 1 : Math.max(0, this.index() - 1));
    this.position.setAttribute('aria-live', manual ? 'polite' : 'off');
    this.track.scrollTo({ left: cards[next].offsetLeft - cards[0].offsetLeft, behavior: this.motion.matches ? 'instant' : 'smooth' });
    this.schedule();
  }
  schedule() {
    clearTimeout(this.timer);
    this.pauseButton.textContent = this.paused ? 'Play autoplay' : 'Pause autoplay';
    this.pauseButton.setAttribute('aria-pressed', String(this.paused));
    this.pauseButton.disabled = this.motion.matches;
    if (this.motion.matches) this.pauseButton.textContent = 'Autoplay off: reduced motion';
    if (this.controls.hidden || this.paused || this.motion.matches || this.hovered || this.focused || !this.visible || document.hidden) return;
    this.timer = setTimeout(() => this.move(1), Math.max(4, Number(this.dataset.interval) || 6) * 1000);
  }
  disconnectedCallback() {
    clearTimeout(this.timer);
    this.controller?.abort();
    this.controller = null;
    this.loading = false;
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.textObserver?.disconnect();
  }
}
if (!customElements.get('casa-review-summary')) customElements.define('casa-review-summary', CasaReviewSummary);
if (!customElements.get('casa-product-reviews')) customElements.define('casa-product-reviews', CasaProductReviews);
